import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Car, UserSearch, Eye, Hand, Loader2, MapPin, X, ShieldAlert } from "lucide-react";
import { t } from "./constants";

const KINDS = [
  { key: "traffic_stop", label: "Traffic Stop", icon: Car, color: "#3B82F6" },
  { key: "subject_stop", label: "Subject Stop", icon: UserSearch, color: "#8B5CF6" },
  { key: "on_view", label: "On-View", icon: Eye, color: "#F59E0B" },
  { key: "flag_down", label: "Flag-Down", icon: Hand, color: "#10B981" },
];
const STATES = ["PA", "MD", "WV", "OH", "NY", "NJ", "DE", "VA", "DC"];
const inp = "w-full bg-[#141518] border border-[#2C2D35] px-2 py-1.5 text-xs outline-none focus:border-[#3B82F6] font-mono text-white";

// One-tap officer-initiated activity. Location comes from the live GPS fix (editable).
export const MdtOnViewCard = ({ open, initialKind, myUnit, fix, onClose, onCreated, onRefreshFix }) => {
  const [kind, setKind] = useState(initialKind || "traffic_stop");
  const [f, setF] = useState({ address: "", plate: "", plate_state: "PA", vehicle: "", occupants: "", notes: "", call_type_code: "", high_risk: false });
  const [types, setTypes] = useState([]);
  const [tq, setTq] = useState("");
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoNote, setGeoNote] = useState("");
  useEffect(() => { if (open) { setKind(initialKind || "traffic_stop"); setF((p) => ({ ...p, address: "", plate: "", vehicle: "", occupants: "", notes: "", call_type_code: "", high_risk: false })); setGeoNote(""); onRefreshFix?.(); } }, [open, initialKind]); // eslint-disable-line
  useEffect(() => {
    if (!open || !["on_view", "flag_down"].includes(kind) || tq.trim().length < 2) { setTypes([]); return; }
    const h = setTimeout(() => client.get("/call-types-smart-search", { params: { q: tq.trim(), discipline: "police", limit: 8 } }).then((r) => setTypes(r.data.results)).catch(() => {}), 250);
    return () => clearTimeout(h);
  }, [tq, kind, open]);
  if (!open) return null;
  const needsType = ["on_view", "flag_down"].includes(kind);
  const gpsLabel = fix ? `GPS ${fix.lat.toFixed(5)}, ${fix.lng.toFixed(5)}` : "No GPS fix — enter an address";
  const useGps = async () => {
    if (!fix) return;
    setGeoBusy(true);
    try {
      const { data } = await client.get("/geo/reverse", { params: { lat: fix.lat, lng: fix.lng } });
      setF((p) => ({ ...p, address: data.label || data.address }));
      setGeoNote(data.ok ? `Filled from GPS${data.cross_street ? ` · nearest cross-street ${data.cross_street}` : ""} · edit if needed` : "Could not reverse-geocode — coordinates filled");
    } catch { toast.error("Reverse geocode failed"); } finally { setGeoBusy(false); }
  };
  const submit = async () => {
    if (needsType && !f.call_type_code) { toast.error("Pick a call type for this on-view."); return; }
    if (!f.address.trim() && !fix) { toast.error("Enter a location or allow GPS."); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/onview", {
        kind, unit: myUnit, address: f.address.trim(), lat: fix?.lat ?? null, lng: fix?.lng ?? null,
        plate: f.plate, plate_state: f.plate_state, vehicle: f.vehicle, occupants: f.occupants === "" ? null : Number(f.occupants),
        notes: f.notes, call_type_code: f.call_type_code || null, high_risk: f.high_risk,
      });
      toast.success(`${KINDS.find((k) => k.key === kind).label} logged — CAD# ${data.public_ref} (Case ${data.cc_number}) · dispatch alerted · you are ON SCENE`);
      onCreated?.(data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create on-view"); }
    finally { setBusy(false); }
  };
  const K = KINDS.find((k) => k.key === kind);
  return (
    <div data-testid="mdt-onview-card" className="fixed inset-0 z-[3000] flex items-end sm:items-center justify-center bg-black/70 p-2" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg border overflow-hidden" style={{ background: t.panel, borderColor: K.color }} onClick={(e) => e.stopPropagation()}>
        <div className="px-3 py-2 flex items-center justify-between border-b" style={{ borderColor: t.border }}>
          <div className="font-head font-bold text-sm text-white flex items-center gap-2"><K.icon size={16} style={{ color: K.color }} /> OFFICER-INITIATED ACTIVITY · {myUnit}</div>
          <button data-testid="mdt-onview-close" onClick={onClose} className="text-[#9CA3AF] hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-3 space-y-2.5">
          <div className="grid grid-cols-4 gap-1.5">
            {KINDS.map((k) => (
              <button key={k.key} data-testid={`mdt-onview-kind-${k.key}`} onClick={() => setKind(k.key)}
                className="flex flex-col items-center gap-1 py-2 rounded border text-[10px] font-head font-bold"
                style={kind === k.key ? { borderColor: k.color, background: k.color + "22", color: "#fff" } : { borderColor: t.border, color: t.dim }}>
                <k.icon size={16} style={{ color: k.color }} /> {k.label}
              </button>
            ))}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="font-mono text-[9px] text-[#9CA3AF] uppercase">Location</label>
              <span data-testid="mdt-onview-gps" className={`font-mono text-[9px] flex items-center gap-1 ${fix ? "text-[#10B981]" : "text-[#F59E0B]"}`}><MapPin size={10} /> {gpsLabel}</span>
            </div>
            <div className="flex gap-1.5">
              <input data-testid="mdt-onview-address" className={`${inp} flex-1`} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder={fix ? "Address / cross-street (optional — GPS attached)" : "Address / cross-street"} />
              <button type="button" data-testid="mdt-onview-use-gps" onClick={useGps} disabled={geoBusy || !fix} title={fix ? "Fill address from my GPS position" : "Waiting for GPS fix…"}
                className="shrink-0 px-2.5 rounded text-[11px] font-head font-bold text-white flex items-center gap-1 disabled:opacity-40" style={{ background: "#10B981" }}>
                {geoBusy ? <Loader2 size={13} className="animate-spin" /> : <MapPin size={13} />} Use my GPS
              </button>
            </div>
            {geoNote && <div data-testid="mdt-onview-gps-note" className="font-mono text-[9px] mt-1" style={{ color: "#6EE7B7" }}>{geoNote}</div>}
          </div>
          {needsType && (
            <div>
              <label className="font-mono text-[9px] text-[#9CA3AF] uppercase">Call type</label>
              <input data-testid="mdt-onview-type-search" className={inp} value={tq} onChange={(e) => { setTq(e.target.value); setF({ ...f, call_type_code: "" }); }} placeholder="Search — suspicious vehicle, disabled motorist, assist citizen…" />
              {types.length > 0 && !f.call_type_code && (
                <div className="border border-[#2C2D35] max-h-32 overflow-y-auto">
                  {types.map((c) => <button key={c.code} data-testid={`mdt-onview-type-${c.code}`} onClick={() => { setF({ ...f, call_type_code: c.code }); setTq(`${c.code} ${c.label}`); }} className="w-full text-left px-2 py-1 text-[11px] font-mono text-[#C9CDD4] hover:bg-[#1E1F24]"><b>{c.code}</b> {c.label} · P{c.priority}</button>)}
                </div>
              )}
            </div>
          )}
          {(kind === "traffic_stop" || kind === "on_view") && (
            <div className="grid grid-cols-[70px_1fr_1fr] gap-1.5">
              <select data-testid="mdt-onview-state" className={inp} value={f.plate_state} onChange={(e) => setF({ ...f, plate_state: e.target.value })}>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
              <input data-testid="mdt-onview-plate" className={`${inp} uppercase tracking-widest`} value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} placeholder="PLATE" />
              <input data-testid="mdt-onview-vehicle" className={inp} value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value })} placeholder="Color / make / model" />
            </div>
          )}
          <div className="grid grid-cols-[90px_1fr] gap-1.5">
            <input data-testid="mdt-onview-occupants" type="number" min="0" className={inp} value={f.occupants} onChange={(e) => setF({ ...f, occupants: e.target.value })} placeholder="Occ." />
            <input data-testid="mdt-onview-notes" className={inp} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" />
          </div>
          {(kind === "traffic_stop" || kind === "subject_stop") && (
            <label data-testid="mdt-onview-highrisk" className={`flex items-center gap-2 px-2 py-1.5 border rounded text-xs font-head cursor-pointer ${f.high_risk ? "border-[#DC2626] bg-[#DC2626]/15 text-white" : "border-[#2C2D35] text-[#9CA3AF]"}`}>
              <input type="checkbox" checked={f.high_risk} onChange={(e) => setF({ ...f, high_risk: e.target.checked })} className="accent-[#DC2626]" />
              <ShieldAlert size={14} className="text-[#DC2626]" /> High-risk stop — request cover unit now
            </label>
          )}
          <div className="font-mono text-[9px] text-[#6B7280]">You will be placed ON SCENE and dispatch alerted{(kind === "traffic_stop" || kind === "subject_stop") ? " · 5-min safety timer starts automatically" : ""}.</div>
          <button data-testid="mdt-onview-submit" onClick={submit} disabled={busy}
            className="w-full py-2.5 rounded font-head font-bold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: K.color }}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <K.icon size={15} />} Go Out — {K.label}
          </button>
        </div>
      </div>
    </div>
  );
};
